// Super Admin → "Messaggi preferiti" (TODO #10): tutti i messaggi di chat che
// clienti e consulenti hanno aggiunto ai preferiti, con filtri e apertura
// della conversazione completa (messaggio preferito evidenziato).
import { useState } from 'react';
import { Spinner } from '@klaro7/ui';
import type { AdminStarredMessage, ChatParticipantRole } from '@klaro7/shared-types';

import { AdminConversationDrawer } from '@/features/chat/components/AdminConversationDrawer';
import { StarIcon } from '@/features/chat/components/StarIcon';
import { formatDateTime, roleLabel } from '@/features/chat/lib/adminChat';
import { useAdminStarred, useAdminStarredFacets } from '@/features/chat/hooks/useStarredMessages';

interface Selection {
  conversationId: string;
  messageId: string;
}

const PAGE_SIZE = 20;

function RoleBadge({ role }: { role: ChatParticipantRole }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
        role === 'user' ? 'bg-sky-50 text-sky-700' : 'bg-brand-teal/10 text-brand-teal-600'
      }`}
    >
      {roleLabel[role]}
    </span>
  );
}

const inputClass =
  'w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/20';

export function AdminStarredMessagesPage() {
  const [userId, setUserId] = useState('');
  const [professionalId, setProfessionalId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<Selection | null>(null);

  const facets = useAdminStarredFacets();
  const { data, isLoading, isError, isFetching } = useAdminStarred({
    userId: userId || undefined,
    professionalId: professionalId || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const hasFilters = !!(userId || professionalId || from || to);
  const resetFilters = () => {
    setUserId('');
    setProfessionalId('');
    setFrom('');
    setTo('');
    setPage(1);
  };
  const withReset = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(1);
  };

  const items: AdminStarredMessage[] = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 p-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
          <StarIcon filled className="h-6 w-6 text-brand-orange" />
          Messaggi preferiti
        </h1>
        <p className="mt-1 text-sm text-white/70">
          I messaggi di chat che clienti e consulenti hanno aggiunto ai preferiti.
        </p>
      </div>

      {/* Filtri */}
      <div className="rounded-xl border border-neutral-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-neutral-500">Utente</span>
            <select
              className={inputClass}
              value={userId}
              onChange={(e) => withReset(setUserId)(e.target.value)}
            >
              <option value="">Tutti gli utenti</option>
              {facets.data?.users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-neutral-500">Consulente</span>
            <select
              className={inputClass}
              value={professionalId}
              onChange={(e) => withReset(setProfessionalId)(e.target.value)}
            >
              <option value="">Tutti i consulenti</option>
              {facets.data?.professionals.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-neutral-500">Dal</span>
            <input
              type="date"
              className={inputClass}
              value={from}
              max={to || undefined}
              onChange={(e) => withReset(setFrom)(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-neutral-500">Al</span>
            <input
              type="date"
              className={inputClass}
              value={to}
              min={from || undefined}
              onChange={(e) => withReset(setTo)(e.target.value)}
            />
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={resetFilters}
              disabled={!hasFilters}
              className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Azzera filtri
            </button>
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3">
          <p className="text-sm text-neutral-500">
            {pagination ? `${pagination.total} messaggi` : '—'}
          </p>
          {isFetching && !isLoading && <Spinner size="sm" />}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <p className="py-16 text-center text-sm text-neutral-500">Impossibile caricare i messaggi preferiti.</p>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm font-medium text-neutral-700">Nessun messaggio preferito</p>
            <p className="mt-1 text-xs text-neutral-400">
              {hasFilters
                ? 'Prova a modificare i filtri.'
                : 'Compariranno qui quando clienti o consulenti metteranno la stella a un messaggio.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  onClick={() => setSelection({ conversationId: it.conversation.id, messageId: it.message.id })}
                  className="grid w-full grid-cols-1 gap-2 px-5 py-4 text-left transition hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto] md:items-center md:gap-4"
                >
                  <div className="min-w-0">
                    <p className="line-clamp-2 text-sm text-neutral-900">{it.message.content}</p>
                    <p className="mt-0.5 text-xs text-neutral-400">
                      Scritto da {it.message.senderName} ({roleLabel[it.message.senderRole].toLowerCase()}) ·{' '}
                      {formatDateTime(it.message.createdAt)}
                    </p>
                  </div>
                  <div className="flex min-w-0 items-center gap-2 text-sm">
                    <StarIcon filled className="h-3.5 w-3.5 shrink-0 text-brand-orange" />
                    <span className="truncate text-neutral-700">{it.starredBy.name}</span>
                    <RoleBadge role={it.starredBy.role} />
                  </div>
                  <p className="min-w-0 truncate text-sm text-neutral-600">
                    {it.conversation.user.name} ↔ {it.conversation.professional.name}
                  </p>
                  <p className="whitespace-nowrap text-xs text-neutral-500">{formatDateTime(it.starredAt)}</p>
                </button>
              </li>
            ))}
          </ul>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-neutral-100 px-5 py-3 text-sm">
            <button
              type="button"
              disabled={!pagination.hasPrevPage}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg px-3 py-1.5 font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-40"
            >
              ← Precedenti
            </button>
            <span className="text-neutral-500">
              Pagina {pagination.page} di {pagination.totalPages}
            </span>
            <button
              type="button"
              disabled={!pagination.hasNextPage}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg px-3 py-1.5 font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-40"
            >
              Successivi →
            </button>
          </div>
        )}
      </div>

      {selection && (
        <AdminConversationDrawer
          conversationId={selection.conversationId}
          highlightMessageId={selection.messageId}
          onClose={() => setSelection(null)}
        />
      )}
    </div>
  );
}
